import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  InputBase,
  Badge,
  Collapse,
  useTheme,
  alpha,
  Button,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  ExpandLess,
  ExpandMore,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Fullscreen as FullscreenIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  FullscreenExit,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme as useCustomTheme } from "../contexts/ThemeContext";
import { usePushNotifications } from "../contexts/PushNotificationsContext";
import { modulesAPI } from "../utils/api";
import { getPushErrorMessage } from "../services/firebaseMessaging";
import { notificationSwal } from "../utils/swal-helpers";
import { getIcon } from "../config/moduleConfig";
import { DynamicRoutes } from "../routes/DynamicRouteGenerator";
import { findFirstValidRoute } from "../utils/navigationUtils";
import Logo from "../components/Logo";
import NotificationMenu from "../components/NotificationMenu";
import Swal from "sweetalert2";

const drawerWidth = 260;
const collapsedDrawerWidth = 64;

const getFullscreenElement = () =>
  document.fullscreenElement ||
  document.webkitFullscreenElement ||
  document.mozFullScreenElement ||
  document.msFullscreenElement;

const enterFullscreen = async (element = document.documentElement) => {
  const requestFullscreen =
    element.requestFullscreen ||
    element.webkitRequestFullscreen ||
    element.mozRequestFullScreen ||
    element.msRequestFullscreen;

  if (!requestFullscreen) return false;

  try {
    await requestFullscreen.call(element);
    return true;
  } catch {
    return false;
  }
};

const exitFullscreen = async () => {
  const cancelFullscreen =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.mozCancelFullScreen ||
    document.msExitFullscreen;

  if (!cancelFullscreen) return false;

  try {
    await cancelFullscreen.call(document);
    return true;
  } catch {
    return false;
  }
};

export const DashboardLayout = () => {
  const theme = useTheme();
  const { isDarkMode, toggleDarkMode } = useCustomTheme();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const {
    available: pushAvailable,
    pushEnabled,
    saving: savingPush,
    togglePushNotifications,
  } = usePushNotifications();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedItems, setExpandedItems] = useState([]);
  const [menuModules, setMenuModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentDrawerWidth = sidebarCollapsed
    ? collapsedDrawerWidth
    : drawerWidth;

  useEffect(() => {
    loadMenuModules();
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!getFullscreenElement());
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, []);

  const loadMenuModules = async () => {
    try {
      setLoading(true);
      const response = await modulesAPI.menu();
      const modules = response.data.data || [];
      setMenuModules(modules);
      const firstRoute = findFirstValidRoute(modules);
      const isAtDashboardRoot =
        location.pathname === "/dashboard" ||
        location.pathname === "/dashboard/";
      if (isAtDashboardRoot && firstRoute && firstRoute !== "/dashboard") {
        navigate(firstRoute, { replace: true });
      }
    } catch (error) {
      console.error("Error loading menu modules:", error);
      setMenuModules([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFullscreen = () => {
    if (!isFullscreen) {
      enterFullscreen();
    } else {
      exitFullscreen();
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePushToggle = async (event) => {
    const enabled = event.target.checked;
    try {
      await togglePushNotifications(enabled);
      notificationSwal(
        enabled ? "Push activado" : "Push desactivado",
        enabled
          ? "Este navegador recibirá notificaciones push."
          : "Las notificaciones volverán a enviarse por WhatsApp.",
        "success",
      );
    } catch (error) {
      notificationSwal("Error", getPushErrorMessage(error), "error");
    }
  };

  const handleLogout = () => {
    handleClose();
    Swal.fire({
      title: "¿Estás seguro?",
      text: "Estás a punto de cerrar tu sesión.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, cerrar sesión",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        logout();
      }
    });
  };

  const handleExpandClick = (text) => {
    if (sidebarCollapsed) return;
    setExpandedItems((prev) =>
      prev.includes(text)
        ? prev.filter((item) => item !== text)
        : [...prev, text]
    );
  };

  const renderMenuItem = (item, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.name);
    const isActive = item.route && location.pathname === item.route;

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding sx={{ display: "block" }}>
          <ListItemButton
            sx={{
              minHeight: 40,
              justifyContent: sidebarCollapsed ? "center" : "initial",
              px: sidebarCollapsed ? 1 : 2,
              py: 0.5,
              pl: sidebarCollapsed ? 1 : depth * 2 + 2,
              backgroundColor: isActive
                ? alpha(theme.palette.primary.main, 0.08)
                : "transparent",
              borderRadius: 1,
              mx: sidebarCollapsed ? 0.5 : 1,
              mb: 0.5,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
            onClick={() => {
              if (hasChildren && !sidebarCollapsed) {
                handleExpandClick(item.name);
              } else if (item.route) {
                navigate(item.route);
              }
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: sidebarCollapsed ? 0 : 1.5,
                justifyContent: "center",
                color: isActive
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
                fontSize: "1.2rem",
              }}
            >
              {getIcon(item.icon)}
            </ListItemIcon>
            {!sidebarCollapsed && (
              <>
                <ListItemText
                  primary={item.name}
                  sx={{
                    color: isActive
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    "& .MuiListItemText-primary": {
                      fontWeight: isActive ? 600 : 400,
                      fontSize: "0.875rem",
                    },
                  }}
                />
                {hasChildren && (
                  <IconButton
                    size="small"
                    sx={{ color: theme.palette.text.secondary, p: 0 }}
                  >
                    {isExpanded ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </IconButton>
                )}
              </>
            )}
          </ListItemButton>
        </ListItem>
        {hasChildren && !sidebarCollapsed && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children?.map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const renderModule = (module) => {
    const visibleItems =
      module.children?.filter(
        (item) => item.status === "active" && item.show_in_menu
      ) || [];

    if (module.type === "module" && visibleItems.length === 0) return null;

    if (module.type === "page") {
      return renderMenuItem(module);
    }

    return (
      <Box key={module.id} sx={{ mb: 2 }}>
        {!sidebarCollapsed && (
          <Typography
            variant="caption"
            sx={{
              px: 2,
              py: 1,
              display: "block",
              color: theme.palette.text.secondary,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: "0.75rem",
            }}
          >
            {module.name}
          </Typography>
        )}
        <List dense>{visibleItems.map((item) => renderMenuItem(item))}</List>
      </Box>
    );
  };

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: sidebarCollapsed ? 1 : 2,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          justifyContent: sidebarCollapsed ? "center" : "flex-start",
        }}
      >
        <Box
          sx={{
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: sidebarCollapsed ? 0 : 1,
            overflow: "hidden",
            width: 32,
            height: 32,
          }}
        >
          {user?.business?.logo_url ? (
            <img src={user.business.logo_url} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <Logo height={32} />
          )}
        </Box>
        {!sidebarCollapsed && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textTransform: "uppercase",
              fontSize: "0.9rem",
              lineHeight: 1.1
            }}
          >
            {user?.business?.name || "STOCK MASTER"}
          </Typography>
        )}
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", py: 1 }}>
        {loading ? (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Cargando menú...
            </Typography>
          </Box>
        ) : (
          menuModules.map((module) => renderModule(module))
        )}
      </Box>

      <Box sx={{ p: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
        <IconButton
          onClick={handleSidebarToggle}
          sx={{
            width: "100%",
            justifyContent: "center",
            color: theme.palette.text.secondary,
          }}
        >
          {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: "none",
          borderBottom: `1px solid ${theme.palette.divider}`,
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: "64px !important" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              position: "relative",
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.common.black, 0.04),
              "&:hover": {
                backgroundColor: alpha(theme.palette.common.black, 0.06),
              },
              marginLeft: 0,
              display: { xs: "none", sm: "block" },
              width: "100%",
              maxWidth: 300,
            }}
          >
            <Box
              sx={{
                padding: theme.spacing(0, 2),
                height: "100%",
                position: "absolute",
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SearchIcon fontSize="small" />
            </Box>
            <InputBase
              placeholder="Search"
              sx={{
                color: "inherit",
                width: "100%",
                "& .MuiInputBase-input": {
                  padding: theme.spacing(1, 1, 1, 0),
                  paddingLeft: `calc(1em + ${theme.spacing(4)})`,
                  fontSize: "0.875rem",
                },
              }}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Toggle Dark Mode">
              <IconButton color="inherit" onClick={toggleDarkMode}>
                {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip
              title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
            >
              <IconButton
                color="inherit"
                onClick={handleToggleFullscreen}
                aria-label={
                  isFullscreen
                    ? "Salir de pantalla completa"
                    : "Activar pantalla completa"
                }
                sx={{ flexShrink: 0 }}
              >
                {isFullscreen ? <FullscreenExit /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>

            {/* Notification Menu Component */}
            <NotificationMenu />

            <Tooltip title="Profile">
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                sx={{ ml: 1 }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: theme.palette.primary.main,
                    fontSize: "0.875rem",
                  }}
                >
                  {user?.first_name?.charAt(0)}
                  {user?.last_name?.charAt(0)}
                </Avatar>
              </IconButton>
            </Tooltip>

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  width: 290,
                  mt: 1,
                },
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Hola, {user?.first_name} {user?.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.roles?.[0]?.name || "User"}
                </Typography>
              </Box>

              <Box sx={{ p: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isDarkMode}
                      onChange={toggleDarkMode}
                      color="primary"
                    />
                  }
                  label="Modo Oscuro"
                  sx={{ mb: 1 }}
                />
                {pushAvailable && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pushEnabled}
                        disabled={savingPush}
                        onChange={handlePushToggle}
                        color="primary"
                      />
                    }
                    label="Notificaciones push"
                  />
                )}
              </Box>

              <Divider />

              <MenuItem onClick={() => navigate("/dashboard/settings")}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Configuración de Cuenta
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Cerrar Sesión
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { sm: currentDrawerWidth },
          flexShrink: { sm: 0 },
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: currentDrawerWidth,
              borderRight: `1px solid ${theme.palette.divider}`,
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          mt: "64px",
          backgroundColor: theme.palette.background.default,
          minHeight: "calc(100vh - 64px)",
          overflowX: "auto",
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "calc(100vh - 64px)",
            }}
          >
            {/* <CircularProgress /> */}
          </Box>
        ) : menuModules.length > 0 ? (
          <DynamicRoutes navConfig={menuModules} />
        ) : (
          <Navigate to="/unauthorized" replace />
        )}
      </Box>
    </Box>
  );
};
